import { FullScreenDialog } from '@/components/ui/full-screen-dialog';
import { Button } from '@/components/ui/button';
import { IcsFormPdfPreviewProvider } from 'components/IAP/context/IcsFormPdfPreviewContext';
import { IncidentScopeProvider } from 'components/IAP/hooks/incidentScope';
import { useScopedIncident } from 'components/IAP/hooks/incidentHooks';
import ICSContextProvider from 'components/IAP/planningView/ICSContext';
import { PLANNING_PHASES } from 'components/IAP/planningView/USCGPage';
import {
  RenderWidget,
  useWidgetRenderContext,
} from 'components/IAP/planningView/widgetRegistry';
import type { PlanLibraryForm } from 'hooks/planLibrary/planLibraryHooks';

function PlanFormCard({ widgetName }: { widgetName: string }) {
  const incident = useScopedIncident();
  const ctx = useWidgetRenderContext(PLANNING_PHASES, 0);
  if (!incident) {
    return <p className="tw:text-sm tw:text-muted-foreground">Loading form…</p>;
  }
  return <RenderWidget widgetName={widgetName} ctx={ctx} />;
}

/**
 * A plan's ICS form, rendered with the workspace card itself against the plan's
 * template incident. Saving, signing, and version history behave exactly as in
 * a workspace; applying the plan clones what is authored here.
 */
export function PlanFormView({
  planName,
  form,
  templateIncidentId,
  onClose,
}: {
  planName: string;
  form: PlanLibraryForm;
  templateIncidentId: string;
  onClose: () => void;
}) {
  return (
    <FullScreenDialog
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title={form.label}
      description={`Template form for ${planName}. Applying the plan copies this form into the workspace as a new draft.`}
      bodyClassName="tw:!min-h-0 tw:!overflow-y-auto tw:!p-3"
      footer={
        <Button type="button" size="sm" onClick={onClose}>
          Done
        </Button>
      }
    >
      <IncidentScopeProvider incidentId={templateIncidentId}>
        <ICSContextProvider>
          <IcsFormPdfPreviewProvider>
            <PlanFormCard widgetName={form.widgetName} />
          </IcsFormPdfPreviewProvider>
        </ICSContextProvider>
      </IncidentScopeProvider>
    </FullScreenDialog>
  );
}
